import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash, MagnifyingGlass, Money, CreditCard, CheckCircle, Spinner, ArrowsClockwise, FilePdf, Image, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { generatePaymentReceipt } from '../utils/receiptGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const paymentMethods = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartão', label: 'Cartão' },
  { value: 'transferência', label: 'Transferência' }
];

const emptyPayment = {
  student_id: '',
  amount: '',
  payment_date: new Date().toISOString().split('T')[0],
  reference_month: new Date().toISOString().slice(0, 7),
  payment_method: 'pix',
  notes: ''
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [formData, setFormData] = useState(emptyPayment);
  const [saving, setSaving] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/students`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data);
      setStudents(studentsRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll payment status when returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, paymentId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.info('Verificação de pagamento expirou. Verifique o status manualmente.');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/stripe/status/${sessionId}`, { withCredentials: true });

      if (data.payment_status === 'paid') {
        setPollingPayment(false);
        toast.success('Pagamento via cartão confirmado com sucesso!');
        fetchData();
        // Clear URL params
        setSearchParams({});
        return;
      } else if (data.status === 'expired') {
        setPollingPayment(false);
        toast.error('Sessão de pagamento expirada.');
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, paymentId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, paymentId, attempts + 1), pollInterval);
      } else {
        setPollingPayment(false);
        toast.error('Erro ao verificar status do pagamento.');
      }
    }
  }, [fetchData, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for Stripe return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentId = searchParams.get('payment_id');
    if (sessionId && paymentId) {
      setPollingPayment(true);
      pollPaymentStatus(sessionId, paymentId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleOpenCreate = () => {
    setCurrentPayment(null);
    setFormData(emptyPayment);
    setDialogOpen(true);
  };

  const handleOpenEdit = (payment) => {
    setCurrentPayment(payment);
    setFormData({
      student_id: payment.student_id,
      amount: payment.amount.toString(),
      payment_date: payment.payment_date,
      reference_month: payment.reference_month,
      payment_method: payment.payment_method,
      status: payment.status || 'pendente',
      notes: payment.notes || ''
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (payment) => {
    setCurrentPayment(payment);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      if (currentPayment) {
        await axios.put(`${API}/payments/${currentPayment.id}`, payload, { withCredentials: true });
        toast.success('Pagamento atualizado com sucesso');
      } else {
        await axios.post(`${API}/payments`, payload, { withCredentials: true });
        toast.success('Pagamento registrado com sucesso');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar pagamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/payments/${currentPayment.id}`, { withCredentials: true });
      toast.success('Pagamento removido com sucesso');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover pagamento');
    }
  };

  const handleGenerateMonthly = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/payments/generate-monthly`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Erro ao gerar mensalidades');
    } finally {
      setGenerating(false);
    }
  };

  const handleSyncPayments = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/stripe/sync-pending`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Erro ao sincronizar pagamentos');
    } finally {
      setSyncing(false);
    }
  };

  const handleStripePayment = async (payment) => {
    setStripeLoading(payment.id);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API}/stripe/checkout`, {
        payment_id: payment.id,
        origin_url: originUrl
      }, { withCredentials: true });

      if (data.url) {
        const a = document.createElement('a');
        a.href = data.url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.error('Erro: URL de pagamento nao recebida.');
        setStripeLoading(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar pagamento com cartao');
      setStripeLoading(null);
    }
  };

  const [sortMode, setSortMode] = useState('recent'); // 'recent' | 'name-asc' | 'name-desc'
  const [monthFilter, setMonthFilter] = useState(() => new Date().toISOString().slice(0, 7));
  const [statusFilter, setStatusFilter] = useState('all'); // all | pago | pendente
  const [sendingEmailId, setSendingEmailId] = useState(null);

  // Compute distinct months from payments (newest first)
  const distinctMonths = Array.from(new Set(payments.map(p => p.reference_month).filter(Boolean))).sort().reverse();
  // Always include current month even if no payments yet
  const currentMonth = new Date().toISOString().slice(0, 7);
  if (!distinctMonths.includes(currentMonth)) distinctMonths.unshift(currentMonth);

  const monthLabel = (ym) => {
    if (!ym || ym === 'all') return 'Todos os meses';
    const [y, m] = ym.split('-');
    const names = ['Janeiro','Fevereiro','Marco','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return `${names[parseInt(m,10)-1]} / ${y}`;
  };

  const filteredPayments = payments.filter(p => {
    if (monthFilter !== 'all' && p.reference_month !== monthFilter) return false;
    if (statusFilter !== 'all' && (p.status || 'pendente') !== statusFilter) return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return (p.student_name || '').toLowerCase().includes(t) || (p.reference_month || '').includes(search);
  });
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    if (sortMode === 'name-asc') return (a.student_name || '').localeCompare(b.student_name || '', 'pt-BR');
    if (sortMode === 'name-desc') return (b.student_name || '').localeCompare(a.student_name || '', 'pt-BR');
    return (b.reference_month || '').localeCompare(a.reference_month || '');
  });

  const paidCount = filteredPayments.filter(p => p.status === 'pago').length;
  const pendingCount = filteredPayments.filter(p => (p.status || 'pendente') === 'pendente').length;
  const totalRevenue = filteredPayments.filter(p => p.status === 'pago').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = filteredPayments.filter(p => p.status !== 'pago').reduce((sum, p) => sum + p.amount, 0);

  const sendChargeReminder = async (payment) => {
    // Need student_id to use the per-student charge-email endpoint
    if (!payment.student_id) {
      toast.error('Aluno sem id - nao foi possivel enviar');
      return;
    }
    setSendingEmailId(payment.id);
    try {
      const subject = `Mensalidade ${monthLabel(payment.reference_month)} - Tia Karol Sports`;
      const description = `Mensalidade ${monthLabel(payment.reference_month)}`;
      const message = `Ola ${payment.student_name || 'Atleta'}, sua mensalidade de ${monthLabel(payment.reference_month)} no valor de $ ${Number(payment.amount).toFixed(2)} esta pendente. Acesse o Portal para regularizar.`;
      await axios.post(
        `${API}/admin/students/${payment.student_id}/charge-email`,
        { subject, message, amount: payment.amount, description },
        { withCredentials: true }
      );
      toast.success('E-mail de cobranca enviado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar e-mail');
    } finally {
      setSendingEmailId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="payments-page">
        {/* Polling Banner */}
        {pollingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3" data-testid="payment-polling-banner">
            <div className="w-5 h-5 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#0077B6] font-medium">Verificando status do pagamento...</p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Mensalidades</h1>
            <p className="text-[#73706A] mt-1">Controle de pagamentos da escola</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSyncPayments}
              disabled={syncing}
              variant="outline"
              className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white"
              data-testid="sync-payments-btn"
            >
              {syncing ? (
                <div className="w-4 h-4 border-2 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <ArrowsClockwise size={18} className="mr-2" />
              )}
              Sincronizar Stripe
            </Button>
            <Button
              onClick={handleGenerateMonthly}
              disabled={generating}
              variant="outline"
              className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
              data-testid="generate-monthly-btn"
            >
              {generating ? (
                <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <ArrowsClockwise size={18} className="mr-2" />
              )}
              Gerar Mensalidades do Mês
            </Button>
            <Button
              onClick={handleOpenCreate}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="add-payment-btn"
            >
              <Plus size={20} className="mr-2" />
              Novo Pagamento
            </Button>
          </div>
        </div>

        {/* Stats - clickable to filter by status */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`stat-card text-left transition-all ${statusFilter === 'all' ? 'ring-2 ring-[#E85D04]' : ''}`}
            data-testid="payments-stat-total"
          >
            <p className="text-sm text-[#73706A]">Total ({monthLabel(monthFilter)})</p>
            <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{filteredPayments.length}</p>
          </button>
          <button
            onClick={() => setStatusFilter('pago')}
            className={`stat-card text-left transition-all ${statusFilter === 'pago' ? 'ring-2 ring-[#2D6A4F]' : ''}`}
            data-testid="payments-stat-paid"
          >
            <p className="text-sm text-[#73706A]">Pagos</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{paidCount}</p>
            <p className="text-xs text-[#73706A] mt-1">$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          </button>
          <button
            onClick={() => setStatusFilter('pendente')}
            className={`stat-card text-left transition-all ${statusFilter === 'pendente' ? 'ring-2 ring-[#E85D04]' : ''}`}
            data-testid="payments-stat-pending"
          >
            <p className="text-sm text-[#73706A]">Pendentes</p>
            <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{pendingCount}</p>
            <p className="text-xs text-[#73706A] mt-1">$ {totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          </button>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Balanco</p>
            <p className="text-2xl font-semibold text-[#0077B6] font-['Outfit']">$ {(totalRevenue - totalPending).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>

        {/* Month filter dropdown */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="month-filter-bar">
          <label className="text-sm font-medium text-[#1A1A1A]">Filtrar por mes:</label>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="border border-[#E6E2D8] rounded-md px-3 py-2 text-sm bg-white"
            data-testid="month-filter-select"
          >
            <option value="all">Todos os meses</option>
            {distinctMonths.map(m => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          {monthFilter !== 'all' && (
            <button
              onClick={() => setMonthFilter('all')}
              className="text-xs text-[#0077B6] hover:underline"
              data-testid="month-filter-clear"
            >
              Limpar filtro
            </button>
          )}
        </div>

        {/* Search + Sort */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input
              placeholder="Buscar por aluno ou mês..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-payments-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="payments-sort-controls">
            <button
              onClick={() => setSortMode('recent')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'recent' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="payments-sort-recent"
            >
              Recentes
            </button>
            <button
              onClick={() => setSortMode('name-asc')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="payments-sort-asc"
            >
              A-Z
            </button>
            <button
              onClick={() => setSortMode('name-desc')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="payments-sort-desc"
            >
              Z-A
            </button>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]">
              {search ? 'Nenhum pagamento encontrado' : 'Nenhum pagamento registrado'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Aluno</th>
                    <th className="table-header">Mês Referência</th>
                    <th className="table-header">Valor</th>
                    <th className="table-header">Método</th>
                    <th className="table-header">Data</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPayments.map((payment, index) => (
                    <tr key={payment.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`payment-row-${index}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#2D6A4F] rounded-full flex items-center justify-center">
                            <Money size={18} className="text-white" />
                          </div>
                          <span className="font-medium text-[#1A1A1A]">{payment.student_name}</span>
                        </div>
                      </td>
                      <td className="table-cell text-[#1A1A1A]">{payment.reference_month}</td>
                      <td className="table-cell font-medium text-[#2D6A4F]">
                        $ {payment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="table-cell capitalize">{payment.payment_method}</td>
                      <td className="table-cell">{payment.payment_date}</td>
                      <td className="table-cell">
                        <span className={`badge ${payment.status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {payment.status !== 'pago' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStripePayment(payment)}
                              disabled={stripeLoading === payment.id}
                              className="text-[#E85D04] hover:text-[#DC2F02] hover:bg-orange-50"
                              data-testid={`stripe-payment-${index}`}
                              title="Pagar com cartão"
                            >
                              {stripeLoading === payment.id ? (
                                <div className="w-4 h-4 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <CreditCard size={18} />
                              )}
                            </Button>
                          )}
                          {payment.status === 'pago' && payment.payment_method?.includes('Stripe') && (
                            <span className="text-[#2D6A4F]" title="Pago via cartão">
                              <CheckCircle size={18} weight="fill" />
                            </span>
                          )}
                          {payment.status === 'pago' && payment.zelle_receipt_path && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const token = localStorage.getItem('access_token');
                                window.open(`${API}/portal/zelle-receipt/${payment.id}?auth=${token}`, '_blank');
                              }}
                              className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50"
                              data-testid={`view-zelle-receipt-${index}`}
                              title={`Ver Comprovante Zelle: ${payment.zelle_receipt_filename || 'comprovante'}`}
                            >
                              <Image size={18} />
                            </Button>
                          )}
                          {payment.status === 'pago' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                try {
                                  generatePaymentReceipt(payment, { forAdmin: true });
                                  toast.success('Comprovante gerado com sucesso!');
                                } catch (err) {
                                  toast.error('Erro ao gerar comprovante');
                                }
                              }}
                              className="text-[#2D6A4F] hover:text-[#1B4332] hover:bg-green-50"
                              data-testid={`receipt-btn-${index}`}
                              title="Baixar Comprovante"
                            >
                              <FilePdf size={18} />
                            </Button>
                          )}
                          {payment.status !== 'pago' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => sendChargeReminder(payment)}
                              disabled={sendingEmailId === payment.id}
                              className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50"
                              data-testid={`send-charge-email-${index}`}
                              title="Enviar e-mail de cobranca"
                            >
                              {sendingEmailId === payment.id ? (
                                <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <EnvelopeSimple size={18} />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(payment)}
                            className="text-[#0077B6] hover:text-[#03045E] hover:bg-[#F5F2EB]"
                            data-testid={`edit-payment-${index}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDelete(payment)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-payment-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stripe Payment Info */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <CreditCard size={24} className="text-[#E85D04]" />
            <h3 className="font-['Outfit'] font-medium text-lg text-[#1A1A1A]">Pagamento com Cartão</h3>
          </div>
          <p className="text-sm text-[#73706A]">
            Para pagamentos com cartão de crédito/débito, primeiro registre o pagamento como "pendente" e depois clique no ícone do cartão (<CreditCard size={14} className="inline text-[#E85D04]" />) para redirecionar ao checkout seguro do Stripe.
          </p>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {currentPayment ? 'Editar Pagamento' : 'Novo Pagamento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Aluno *</Label>
                <Select
                  value={formData.student_id}
                  onValueChange={(value) => setFormData({ ...formData, student_id: value })}
                  disabled={!!currentPayment}
                >
                  <SelectTrigger data-testid="payment-student-select">
                    <SelectValue placeholder="Selecione um aluno" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(student => (
                      <SelectItem key={student.id} value={student.id}>{student.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>Mês de Referência *</Label>
                <Input
                  type="month"
                  value={formData.reference_month}
                  onChange={(e) => setFormData({ ...formData, reference_month: e.target.value })}
                  required
                  data-testid="payment-month-input"
                />
              </div>
              <div>
                <Label>Data do Pagamento *</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  required
                  data-testid="payment-date-input"
                />
              </div>
              <div>
                <Label>Método</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (
                      <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {currentPayment && (
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="payment-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label>Observações</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field min-h-[80px] resize-none"
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || (!currentPayment && !formData.student_id)}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-payment-btn"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">
            Tem certeza que deseja remover este pagamento de <strong>{currentPayment?.student_name}</strong>?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-[#9B2226] hover:bg-[#7d1c1f]"
              data-testid="confirm-delete-payment-btn"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
